'use strict';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import React, {Component} from 'react';
import * as T from 'prop-types';
import classNames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import {IonKeyboardAttach} from '../../../_libs/reactionic/src/components/ionKeyboardAttach';
import IonSpinner from '../../../_libs/reactionic/src/components/ionSpinner';
import {SyncStatus} from '../../../_libs/redux-observable-list';
import * as FlipMoveConfig from '../../../config/style/FlipMoveListConfig';
import {Button} from '../../../components/buttons/Button';
import {LoadMoreComponent} from '../../../core-components/loading-icon/components/LoadMoreComponent';
import {StatusOverlayComponent} from '../../../core-components/loading-icon/components/StatusOverlayComponent';
import {MeasuredChatView} from '../../../core-components/measured-chat-view/MeasuredChatView';
import {abbreviate} from '../../../core-utilities/filters/filterUtils';
import {AvatarColors} from '../../../core-utilities/domain/AvatarColors';
import {ViewMode} from '../../../core-utilities/enums/ViewMode';
import {ChatImageItem} from '../ChatImageItem';
import {ChatTextItem} from '../ChatTextItem';
import {ChatMessageInput} from '../ChatMessageInput';


const COMMENT_READ_TIMEOUT = 750;

export class BaseChatListComponent extends Component {
  static propTypes = {
    className: T.string,
    keyboardHeight: T.number,
    maxLength: T.number,
    allowNav: T.bool,
    allowVideoChat: T.bool,
    joinVideoChat: T.bool,
    inVideoChat: T.bool,
    showSubHeader: T.bool,
    onClose: T.func,
    onSubmit: T.func,
    onMarkCommentsRead: T.func,
    onClick: T.func,
    onNav: T.func,
    onStartVideoSession: T.func,
    onJoinVideoSession: T.func,
    onShowSubscribers: T.func,
    onShowSettings: T.func,
  };

  static defaultProps = {
    maxLength: 2048,
    keyboardHeight: 0,
  };

  static defaultInputStyle = {};

  static getInputStyle(keyboardHeight) {
    return {
      style: keyboardHeight === 0
        ? BaseChatListComponent.defaultInputStyle
        : {marginBottom: keyboardHeight + 'px'},
    };
  }

  constructor(props) {
    super(props);
    this.state = BaseChatListComponent.getInputStyle(props.keyboardHeight);
    this.renderItem = this.renderItem.bind(this);
    this.handleNav = this.handleNav.bind(this);
    this.handleMarkCommentsRead = this.handleMarkCommentsRead.bind(this);
    this.handleClickVideoChat = this.handleClickVideoChat.bind(this);
    this.setState = this.setState.bind(this);
  }

  handleNav() {
    if (isFunction(this.props.onNav)) {
      this.props.onNav(this.props.channel);
    }
  }

  handleMarkCommentsRead(count) {
    if (isFunction(this.props.onMarkCommentsRead)) {
      this.props.onMarkCommentsRead(this.props.parentId, count);
    }
  }

  handleClickVideoChat(e) {
    if (this.props.joinVideoChat) {
      if (isFunction(this.props.onJoinVideoSession)) {
        this.props.onJoinVideoSession(this.props.channel);
      }
    } else {
      if (isFunction(this.props.onStartVideoSession)) {
        this.props.onStartVideoSession(this.props.channel);
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    this.props$.next(nextProps);
  }

  componentDidMount() {
    this.props$ = new ReplaySubject(1);
    this.props$.next(this.props);
    this.readSubscription = this.props$
      .filter(props => props.mode === ViewMode.SUCCESS && !props.isLoadingReadCount)
      .take(1)
      .mergeMap(() => this.props$)
      .distinctUntilChanged(null, props => props.total)
      .filter(props => isNumber(props.total)
        && props.total > 0
        && (!isNumber(props.readCount) || props.total > props.readCount)
      )
      .map(props => props.total)
      .delay(COMMENT_READ_TIMEOUT)
      .throttleTime(COMMENT_READ_TIMEOUT, void 0, {leading: true, trailing: true})
      .subscribe(this.handleMarkCommentsRead);

    this.stateSubscription = this.props$
      .map(props => props.keyboardHeight)
      .distinctUntilChanged()
      .map(BaseChatListComponent.getInputStyle)
      .subscribe(this.setState);
  }

  componentWillUnmount() {
    this.props$.complete();
    this.props$ = null;
    this.readSubscription && this.readSubscription.unsubscribe();
    this.stateSubscription && this.stateSubscription.unsubscribe();
  }

  render() {
    // TODO: probably need to make the header configurable
    // TODO: optionally render subheader: video chat button, add person/person count button, and settings button
    // subheader would not show up when used in video chat context

    // TODO: decide whether to display a network warning based on the last sync attempt time
    const listData = this.renderList();

    return (
      <div
        className={classNames("ChatList", this.props.className)}
        onClick={this.props.onClick}
      >
        {this.renderHeader()}
        {this.props.showSubHeader ? this.renderSubHeader() : null}
        <StatusOverlayComponent
          showNoData={false}
          mode={this.props.mode}
        />
        <MeasuredChatView
          className="ChatList__scrollPanel"
          flipped={true}
          reversed={true}
          autoScroll={false}
          scrollLoadThreshold={50}
          useSmoothScrollingWrapper={false}
          onInfiniteLoad={this.props.onLoadMore}
          usePropLoading={true}
          loadingSpinnerDelegate={<LoadMoreComponent mode={this.props.mode} />}
          isInfiniteLoading={this.props.mode === ViewMode.LOADING_MORE}
        >
          <ReactCSSTransitionGroup
            transitionName={FlipMoveConfig.transitionName}
            transitionAppear={FlipMoveConfig.transitionAppear}
            transitionAppearTimeout={FlipMoveConfig.transitionAppearTimeout}
            transitionEnterTimeout={FlipMoveConfig.transitionEnterTimeout}
            transitionLeaveTimeout={FlipMoveConfig.transitionLeaveTimeout}
            className="ChatList__scroll list"
          >
            <div className="ots-messages-alert ots-hidden">
              Messages will be delivered when other users arrive
            </div>
            {listData.list}
          </ReactCSSTransitionGroup>
        </MeasuredChatView>
        {listData.pending === 0 ? null : (
          <IonSpinner
            className="ChatList__pending"
            icon="crescent"
          />
        )}
        <ChatMessageInput
          style={this.state.style}
          disabled={!this.props.init}
          channel={this.props.channel}
          onSubmit={this.props.onSubmit}
          maxLength={this.props.maxLength}
        />
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="ChatList__header">
        <Button
          className="IconButton ChatList__headerButton close-icon"
          iconClass="ion-android-close"
          onClick={this.props.onClose}
        />
        <span className="VideoChat-chat-header-title">
          {get(this.props.channel, 'name', 'Chat')}
        </span>
        {this.props.init && this.props.allowNav ? (
          <Button
            className="IconButton ChatList__headerButton"
            iconClass="icon ion-ios-arrow-forward"
            onClick={this.handleNav}
          />
        ) : null}
      </div>
    );
  }

  renderSubHeader() {
    const props = this.props;
    // TODO: show message corresponding in or join prompt
    return (
      <div className="ChatList__subHeader">
        <Button
          className="IconButton ChatList__headerButton"
          iconClass={classNames("icon ion-ios-videocam", {
            ChatList__headerButton_join: props.joinVideoChat || props.inVideoChat,
            ChatList__headerButton_disabled: !props.allowVideoChat,
          })}
          disabled={!props.allowVideoChat}
          title={getVideoTitle(props)}
          onClick={this.handleClickVideoChat}
        />
        <Button
          className="IconButton ChatList__headerButton"
          iconClass="icon ion-person-stalker"
          onClick={this.props.onShowSubscribers}
        />
        <Button
          className="IconButton ChatList__headerButtonRight"
          iconClass="icon ion-ios-gear"
          onClick={this.props.onShowSettings}
        />
      </div>
    );
  }

  renderList() {
    let pending = 0;

    const list = this.props.list.map(model => {
      switch (model.syncStatus) {
        case SyncStatus.PENDING:
        case SyncStatus.IN_PROGRESS:
        case SyncStatus.IN_PROGRESS_W_PENDING:
          pending++;
      }

      return this.renderItem(model);
    });

    return {
      list,
      pending,
    };
  }

  renderItem(model) {
    if (!model.author) {
      return null;
    }

    switch (model.type) {
      case 'IMAGE':
        return (
          <ChatImageItem
            key={model.id}
            isAuthor={model.author.id === this.props.userId}
            url={model.url}
            requiresAuth={model.requiresAuth}
            date={model.date}
            sender={model.author.name}
            senderInitial={abbreviate(model.author.name)}
            avatarColor={AvatarColors.getColor(model.author.avc)}
            avatarFontColor={AvatarColors.getFontColor(model.author.avc)}
            avatarUrl={model.author.avatarUrl}
          />
        );

      case 'TEXT':
        return (
          <ChatTextItem
            key={model.id}
            isAuthor={model.author.id === this.props.userId}
            message={model.message}
            callouts={model.callouts}
            date={model.date}
            sender={model.author.name}
            senderInitial={abbreviate(model.author.name)}
            avatarColor={AvatarColors.getColor(model.author.avc)}
            avatarFontColor={AvatarColors.getFontColor(model.author.avc)}
            avatarUrl={model.author.avatarUrl}
          />
        );

      default:
        // for new, unsupported types
        return null;
    }
  }
}

export const ChatListComponent = IonKeyboardAttach(BaseChatListComponent);

function getVideoTitle(props) {
  if (props.permissionsMode === ViewMode.LOADING) {
    return 'Loading...';
  } else if (props.permissionsMode === ViewMode.ENONET) {
    return 'Failed to contact server.';
  } else if (props.permissionsMode === ViewMode.EOTHER) {
    return 'An unexpected error occurred.';
  } else if (!props.allowVideoChat) {
    return 'Video chat is not enabled. Contact support for more information';
  } else if (props.joinVideoChat) {
    return 'Join current video call.';
  } else {
    return 'Start a video call.';
  }
}



// WEBPACK FOOTER //
// ./src/js/comments/components/ChatList/ChatListComponent.js